import { CHUNK_SIZE, HEIGHT_SPACING } from "@eldrun/worldgen";
import { chunkOrigin, TILE_SIDE } from "./tile.ts";

/** LODs do terreno (doc 04): espaçamento 2/4/8/16 m. */
export const LOD_COUNT = 4;
export const lodStep = (lod: number): number => 1 << lod;
/** Quads por lado do chunk no LOD. */
export const lodQuads = (lod: number): number => (TILE_SIDE - 1) / lodStep(lod);
export const lodSpacing = (lod: number): number => HEIGHT_SPACING * lodStep(lod);
/** Saias verticais cobrem frestas entre LODs vizinhos. */
export const skirtDepth = (lod: number): number => Math.max(4, 2 * lodSpacing(lod));

const gridVertexCount = (lod: number): number => (lodQuads(lod) + 1) * (lodQuads(lod) + 1);
export const lodVertexCount = (lod: number): number => gridVertexCount(lod) + 4 * lodQuads(lod);

/**
 * Vértices de borda em volta do chunk, começando em (0, 0): borda z = 0 (x crescente),
 * x = máx (z crescente), z = máx (x decrescente), x = 0 (z decrescente). Visto de cima (+y),
 * esse percurso é horário, com o lado de fora à esquerda.
 */
function borderCells(q: number): [number, number][] {
  const cells: [number, number][] = [];
  for (let i = 0; i < q; i++) cells.push([i, 0]);
  for (let j = 0; j < q; j++) cells.push([q, j]);
  for (let i = q; i > 0; i--) cells.push([i, q]);
  for (let j = q; j > 0; j--) cells.push([0, j]);
  return cells;
}

const indexCache = new Map<number, Uint16Array>();

/**
 * Índices compartilhados por todos os chunks de um LOD. Cada quad é cortado pela diagonal
 * (i, j)–(i+1, j+1), igual à interpolação física (doc 07, A3). Triângulos anti-horários vistos
 * de cima; saias voltadas para fora.
 */
export function lodIndices(lod: number): Uint16Array {
  const cached = indexCache.get(lod);
  if (cached) return cached;
  const q = lodQuads(lod);
  const row = q + 1;
  const v = (i: number, j: number) => j * row + i;
  const indices: number[] = [];
  for (let j = 0; j < q; j++) {
    for (let i = 0; i < q; i++) {
      const a = v(i, j);
      const b = v(i + 1, j);
      const c = v(i + 1, j + 1);
      const d = v(i, j + 1);
      indices.push(a, c, b, a, d, c);
    }
  }
  const border = borderCells(q);
  const skirtBase = gridVertexCount(lod);
  for (let k = 0; k < border.length; k++) {
    const [i0, j0] = border[k] as [number, number];
    const [i1, j1] = border[(k + 1) % border.length] as [number, number];
    const top0 = v(i0, j0);
    const top1 = v(i1, j1);
    const bottom0 = skirtBase + k;
    const bottom1 = skirtBase + ((k + 1) % border.length);
    // Com o lado de fora à esquerda do percurso, esta ordem deixa a face voltada para fora.
    indices.push(top0, top1, bottom0, top1, bottom1, bottom0);
  }
  const result = Uint16Array.from(indices);
  indexCache.set(lod, result);
  return result;
}

export interface ChunkMesh {
  positions: Float32Array;
  colors: Uint8Array;
  /** Normal suave por vértice (Fase 13), Int8 normalizado com 4 componentes (passo de 4 bytes). */
  normals: Int8Array;
}

/**
 * Meia-largura (m) da diferença central da normal suave: o morro lê como rolando, sem a faceta de
 * 2 m (a rede do chão desenha os triângulos grandes por cima).
 */
export const NORMAL_REACH = 4;

/**
 * Posições, cores e normais suaves de um chunk num LOD, a partir do tile de 2 m (amostras a cada 2^lod).
 * A normal é a diferença central a `NORMAL_REACH` m (ou o passo do LOD, se maior); fora do tile, a
 * altura vem de `heightAt` (a mesma função do tile: os vizinhos concordam na borda, sem costura). Sem
 * `heightAt`, a borda repete a amostra do tile mais perto.
 */
export function buildChunkMesh(heights: Float32Array, tileColors: Uint8Array, cx: number, cz: number, lod: number, heightAt?: (x: number, z: number) => number): ChunkMesh {
  const q = lodQuads(lod);
  const step = lodStep(lod);
  const spacing = lodSpacing(lod);
  const x0 = chunkOrigin(cx);
  const z0 = chunkOrigin(cz);
  const total = lodVertexCount(lod);
  const positions = new Float32Array(total * 3);
  const colors = new Uint8Array(total * 4);
  const normals = new Int8Array(total * 4);
  // Altura no nó (ti, tj) da grade de 2 m do tile (pode sair dele).
  const last = TILE_SIDE - 1;
  const at = (ti: number, tj: number): number => {
    if (ti >= 0 && ti <= last && tj >= 0 && tj <= last) return heights[tj * TILE_SIDE + ti] as number;
    if (heightAt) return heightAt(x0 + ti * HEIGHT_SPACING, z0 + tj * HEIGHT_SPACING);
    return heights[Math.min(last, Math.max(0, tj)) * TILE_SIDE + Math.min(last, Math.max(0, ti))] as number;
  };
  const reach = Math.max(1, Math.round(Math.max(NORMAL_REACH, spacing) / HEIGHT_SPACING));
  const write = (n: number, i: number, j: number, drop: number) => {
    const t = j * step * TILE_SIDE + i * step;
    positions[n * 3] = x0 + i * spacing;
    positions[n * 3 + 1] = (heights[t] as number) - drop;
    positions[n * 3 + 2] = z0 + j * spacing;
    colors.set(tileColors.subarray(t * 4, t * 4 + 4), n * 4);
    const ti = i * step;
    const tj = j * step;
    const nx = at(ti - reach, tj) - at(ti + reach, tj);
    const nz = at(ti, tj - reach) - at(ti, tj + reach);
    const ny = 2 * reach * HEIGHT_SPACING;
    const len = Math.hypot(nx, ny, nz);
    normals[n * 4] = Math.round((nx / len) * 127);
    normals[n * 4 + 1] = Math.round((ny / len) * 127);
    normals[n * 4 + 2] = Math.round((nz / len) * 127);
  };
  let n = 0;
  for (let j = 0; j <= q; j++) for (let i = 0; i <= q; i++) write(n++, i, j, 0);
  const depth = skirtDepth(lod);
  for (const [i, j] of borderCells(q)) write(n++, i, j, depth);
  return { positions, colors, normals };
}

/** Garantia de compatibilidade: o tamanho do chunk precisa ser divisível pelo maior passo. */
export const LOD_COMPATIBLE = (CHUNK_SIZE / HEIGHT_SPACING) % lodStep(LOD_COUNT - 1) === 0;
